/**
 * Convert kebab-case or snake_case string to Title Case
 * Example: "physics-and-astronomy" -> "Physics and Astronomy"
 * Example: "smm" -> "SMM" (converts short lowercase words to uppercase acronyms)
 * Example: "SMM" -> "SMM" (preserves existing acronyms)
 * Special cases:
 * - "AND" -> "and" (lowercase)
 * - "DATA" -> "Data"
 * - "MATH" -> "Math"
 * - "CARE" -> "Care"
 * - "WEB" -> "Web"
 * - "UX" and "UI" stay capitalized
 * @param {string} str - The string to format
 * @param {Function} [t] - Optional translation function (e.g., from useTranslate('Directions'))
 * @returns {string} - Formatted string in Title Case or translated string
 */
const formatDirectionName = (str, t) => {
  if (!str || typeof str !== 'string') return str;
  
  // Check for translation first if translation function is provided
  if (t && typeof t === 'function') {
    try {
      // Try original string first
      let translated = t(str);
      if (translated && 
          typeof translated === 'string' && 
          translated !== str && 
          !translated.startsWith('Directions.') &&
          translated.length > 0) {
        return translated;
      }
      
      // Normalize string: lowercase and replace spaces/slashes with hyphens
      // Keep Cyrillic characters for Ukrainian translations
      const normalized = str
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/\//g, '-')
        .replace(/&/g, '-and-')
        .replace(/[^a-zа-яіїєґ0-9-]/gi, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      // Try normalized version
      if (normalized && normalized !== str.toLowerCase()) {
        translated = t(normalized);
        if (translated && 
            typeof translated === 'string' && 
            translated !== normalized && 
            !translated.startsWith('Directions.') &&
            translated.length > 0) {
          return translated;
        }
      }
    } catch (e) {
      // If translation fails, fall through to formatting
    }
  }
  
  // Handle UX/UI variations - normalize to UX/UI
  // Replace various UX/UI patterns with standard UX/UI
  str = str
    .replace(/\b[Uu]x\/[Uu]i\b/g, 'UX/UI')
    .replace(/\b[Uu]i\/[Uu]x\b/g, 'UX/UI')
    .replace(/\b[Uu]x-[Uu]i\b/g, 'UX/UI')
    .replace(/\b[Uu]i-[Uu]x\b/g, 'UX/UI')
    .replace(/\b[Uu]x_[Uu]i\b/g, 'UX/UI')
    .replace(/\b[Uu]i_[Uu]x\b/g, 'UX/UI');
  
  // Handle AI variations - normalize to AI
  str = str.replace(/\b[Aa]i\b/g, 'AI');
  
  // If the entire string is just a UX/UI variation, return it
  if (/^ux\/ui$/i.test(str) || /^ui\/ux$/i.test(str) || 
      /^ux-ui$/i.test(str) || /^ui-ux$/i.test(str) ||
      /^ux_ui$/i.test(str) || /^ui_ux$/i.test(str)) {
    return 'UX/UI';
  }
  
  // Special case mappings for specific words
  const specialCases = {
    'AND': 'and',
    'DATA': 'Data',
    'MATH': 'Math',
    'CARE': 'Care',
    'LAW': 'Law',
    'WEB': 'Web',
    'UX': 'UX',
    'UI': 'UI',
    'AI': 'AI',
    'Ux': 'UX',
    'Ui': 'UI',
    'Ai': 'AI'
  };
  
  return str
    .split(/[-_/]/) // Split on hyphens, underscores, or slashes
    .map(word => {
      const upperWord = word.toUpperCase();
      
      // Check special cases first
      if (specialCases[upperWord]) {
        return specialCases[upperWord];
      }
      
      // Preserve UX, UI, and AI (always uppercase)
      if (upperWord === 'UX' || upperWord === 'UI' || upperWord === 'AI') {
        return upperWord;
      }
      
      // Check if word is a potential acronym (2-4 characters)
      if (word.length >= 2 && word.length <= 4) {
        // If it's all uppercase, preserve it (already an acronym)
        if (word === word.toUpperCase()) {
          return word; // Preserve acronyms like SMM, IT, AI, etc.
        }
        // If it's all lowercase and short, treat as acronym and convert to uppercase
        if (word === word.toLowerCase()) {
          return word.toUpperCase(); // Convert smm -> SMM, it -> IT, etc.
        }
      }
      
      // For regular words, capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' '); // Join with spaces
};

export default formatDirectionName;
